import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { IconPortal } from '../../icons';

import { useAuth } from '../../context/AuthContext';
import { useIsAdvisorContext } from '../../hooks/useIsAdvisorContext';
import { useIsReadOnly } from '../../hooks/useIsReadOnly';
import { hasSellerRequiredFields } from '../../utils/sellerProfile';
import { Card } from '../common/Card';
import { Button } from '../common/Button';

/**
 * `KanzleiProfileCard` - M-UX-7.3 (`plan-ux-7.3-advisor-invoice.md` §2, E2).
 *
 * Non-modal, self-gated nudge shown to a Kanzlei (Steuerberater) in their OWN
 * tenant context: "complete your firm profile to invoice under your own
 * name". This is the visible, voluntary counterpart to the silent
 * onboarding-wizard suppress for advisors in `MainLayout.tsx:124-127`
 * (E1 - that suppress stays untouched; this card is the additional,
 * non-modal path §13.4 (2) asks for).
 *
 * Self-contained (pattern: `components/advisor/AdvisorContextBar.tsx`,
 * 7.1): reads its own state via {@link useAuth}, {@link useIsAdvisorContext},
 * {@link useIsReadOnly} - no prop-drilling from the caller. The caller
 * (`pages/CockpitPage.tsx`) mounts it unconditionally; this component alone
 * decides whether to render (outer/inner-less single-component gate, same
 * "render null" contract as `AdvisorInviteDashboardCard`'s outer gate).
 *
 * Render guard (`null` unless ALL hold):
 * - `sellerDataLoading === false`      (anti-flash, E2/D2)
 * - `isAdvisorContext === true`        (advisor signal, 5-OR hook)
 * - `isReadOnly === false`             (own Kanzlei context, not viewing a client)
 * - `!hasSellerRequiredFields(sellerData)` (own address/company data still incomplete)
 *
 * No write logic whatsoever - the CTA only navigates to `/dashboard/konto`
 * (`AccountPage`, verified E3), which already owns the seller-data form.
 * Dismiss-free by design: the card disappears once the profile is complete,
 * not via a close button.
 *
 * @module components/advisor/KanzleiProfileCard
 */
export interface KanzleiProfileCardProps {
  /** Optional layout hook for the caller; no other required props. */
  className?: string;
}

const KanzleiProfileCard: React.FC<KanzleiProfileCardProps> = ({ className }) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { sellerData, sellerDataLoading } = useAuth();
  const isAdvisorContext = useIsAdvisorContext();
  const isReadOnly = useIsReadOnly();

  const shouldRender =
    !sellerDataLoading && isAdvisorContext && !isReadOnly && !hasSellerRequiredFields(sellerData);

  if (!shouldRender) {
    return null;
  }

  const handleCtaClick = (): void => {
    navigate('/dashboard/konto');
  };

  return (
    <Card
      role="region"
      aria-label={t('kanzleiCard.ariaRegion')}
      className={`kanzlei-profile-card mb-6 flex flex-col gap-4 border-l-4 border-l-indigo-500 sm:flex-row sm:items-center sm:justify-between${
        className ? ` ${className}` : ''
      }`}
    >
      <div className="flex items-start gap-4">
        {/* Mint medallion — the card's one signature move: a capability-unlock
            marker (Mint == brand accent) that lifts the card above the neutral
            surface-raised cockpit tiles without an ad-banner wash (D4). */}
        <span className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-indigo-500/10 text-accent-ink ring-1 ring-indigo-500/20">
          <IconPortal size={20} aria-hidden />
        </span>
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-primary">{t('kanzleiCard.title')}</h3>
          <p className="mt-1 max-w-prose text-sm leading-relaxed text-secondary">
            {t('kanzleiCard.body')}
          </p>
        </div>
      </div>
      <Button
        variant="primary"
        size="md"
        onClick={handleCtaClick}
        // Explicit, self-contained AA focus ring (D3): the shared `common/Button`
        // primitive carries no focus-visible ring, so this conversion-critical CTA
        // gets a Mint ring offset against the card surface here — without touching
        // the shared primitive (would ripple across ~85 call sites).
        className="w-full shrink-0 sm:w-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--surface-raised)]"
      >
        {t('kanzleiCard.cta')}
      </Button>
    </Card>
  );
};

export default KanzleiProfileCard;
